import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Outlet, useNavigate } from "react-router-dom";

import {
  PitchContextFieldLabel,
  PITCH_CONTEXT_FIELD_PLACEHOLDER,
} from "../../components/brand/PitchContextFieldLabel";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import { brandProfileService } from "../../services/brandProfile";

const emptyForm = () => ({
  company_name: "",
  website_url: "",
  core_offering: "",
  icp: "",
  custom_context: "",
});

const SettingsBrand = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accordionInitRef = useRef(false);
  const [openByHash, setOpenByHash] = useState({});
  const [formsByHash, setFormsByHash] = useState({});
  const [deleteError, setDeleteError] = useState("");

  const brandsQuery = useQuery({
    queryKey: ["brand-profiles-all"],
    queryFn: brandProfileService.listAll,
  });

  const brands = useMemo(() => brandsQuery.data?.results ?? [], [brandsQuery.data]);

  useEffect(() => {
    if (!brands.length) {
      accordionInitRef.current = false;
      setOpenByHash({});
      return;
    }
    setOpenByHash((prev) => {
      const next = { ...prev };
      const hashes = brands.map((b) => b.hash);
      const hashSet = new Set(hashes);
      Object.keys(next).forEach((h) => {
        if (!hashSet.has(h)) delete next[h];
      });
      for (const h of hashes) {
        if (!(h in next)) next[h] = false;
      }
      if (!accordionInitRef.current) {
        next[hashes[0]] = true;
        accordionInitRef.current = true;
      }
      return next;
    });
    setFormsByHash((prev) => {
      const next = { ...prev };
      const hashes = new Set(brands.map((b) => b.hash));
      for (const b of brands) {
        if (!next[b.hash]) {
          next[b.hash] = {
            company_name: b.company_name || "",
            website_url: b.website_url || "",
            core_offering: b.core_offering || "",
            icp: b.icp || "",
            custom_context: b.custom_context || "",
          };
        }
      }
      Object.keys(next).forEach((h) => {
        if (!hashes.has(h)) delete next[h];
      });
      return next;
    });
  }, [brands]);

  const saveMutation = useMutation({
    mutationFn: ({ hash, payload }) => brandProfileService.updateByHash(hash, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
      queryClient.invalidateQueries({ queryKey: ["brand-profile"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (hash) => brandProfileService.remove(hash),
    onSuccess: (_data, deletedHash) => {
      setDeleteError("");
      queryClient.setQueryData(["brand-profiles-all"], (old) => {
        if (!old?.results) return old;
        const results = old.results.filter((b) => b.hash !== deletedHash);
        return { ...old, results, count: results.length };
      });
      setOpenByHash((o) => {
        const next = { ...o };
        delete next[deletedHash];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
      queryClient.invalidateQueries({ queryKey: ["brand-profile"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setDeleteError(typeof detail === "string" ? detail : "Could not delete brand.");
    },
  });

  const submitBrand = (e, hash) => {
    e.preventDefault();
    const payload = formsByHash[hash];
    if (!payload) return;
    saveMutation.mutate({ hash, payload });
  };

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">Brands</h1>
        <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
          Train the outbound engine with your product details.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] text-theme-text opacity-60">
          {brands.length} brand{brands.length === 1 ? "" : "s"}
        </p>
        <Button type="button" size="sm" onClick={() => navigate("/settings/brand/new/")}>
          New brand
        </Button>
      </div>

      {deleteError ? <p className="font-mono text-xs text-theme-accent">{deleteError}</p> : null}

      {brandsQuery.isLoading ? (
        <p className="text-theme-muted font-mono text-sm">Loading brands…</p>
      ) : null}

      {brands.length ? (
        <Card className="overflow-hidden p-0 divide-y divide-theme-border">
          {brands.map((b) => {
            const open = Boolean(openByHash[b.hash]);
            const form = formsByHash[b.hash] || emptyForm();
            return (
              <div key={b.hash}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left bg-theme-main hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                  aria-expanded={open}
                  onClick={() => setOpenByHash((o) => ({ ...o, [b.hash]: !o[b.hash] }))}
                >
                  <div className="min-w-0">
                    <p className="font-display font-bold text-lg text-theme-on-background truncate">
                      {b.company_name?.trim() || "Untitled brand"}
                    </p>
                    <p className="font-mono text-[10px] text-theme-text opacity-50 truncate">{b.website_url || "—"}</p>
                  </div>
                  <span className="material-symbols-outlined text-theme-muted shrink-0">{open ? "expand_less" : "expand_more"}</span>
                </button>
                {open ? (
                  <form
                    className="flipmail-brand-accordion-panel p-5 space-y-4 border-t border-theme-border bg-theme-main"
                    onSubmit={(e) => submitBrand(e, b.hash)}
                  >
                    <Input
                      label="Website URL"
                      value={form.website_url}
                      onChange={(e) =>
                        setFormsByHash((old) => ({
                          ...old,
                          [b.hash]: { ...form, website_url: e.target.value },
                        }))
                      }
                      placeholder="https://your-company.com"
                    />
                    <Input
                      label="Company name"
                      value={form.company_name}
                      onChange={(e) =>
                        setFormsByHash((old) => ({
                          ...old,
                          [b.hash]: { ...form, company_name: e.target.value },
                        }))
                      }
                      placeholder="e.g. FlipMail"
                    />
                    <Input
                      label="Product description"
                      multiline
                      value={form.core_offering}
                      onChange={(e) =>
                        setFormsByHash((old) => ({
                          ...old,
                          [b.hash]: { ...form, core_offering: e.target.value },
                        }))
                      }
                      placeholder="Describe what your product does..."
                    />
                    <Input
                      label="Ideal customer profile"
                      multiline
                      value={form.icp}
                      onChange={(e) =>
                        setFormsByHash((old) => ({
                          ...old,
                          [b.hash]: { ...form, icp: e.target.value },
                        }))
                      }
                      placeholder="Who exactly are we pitching?"
                    />
                    <Input
                      label={<PitchContextFieldLabel />}
                      multiline
                      value={form.custom_context}
                      onChange={(e) =>
                        setFormsByHash((old) => ({
                          ...old,
                          [b.hash]: { ...form, custom_context: e.target.value },
                        }))
                      }
                      placeholder={PITCH_CONTEXT_FIELD_PLACEHOLDER}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          setDeleteError("");
                          if (window.confirm("Delete this brand? This cannot be undone.")) {
                            deleteMutation.mutate(b.hash);
                          }
                        }}
                      >
                        Delete brand
                      </Button>
                      <Button type="submit" size="sm" loading={saveMutation.isPending}>
                        Save
                      </Button>
                    </div>
                  </form>
                ) : null}
              </div>
            );
          })}
        </Card>
      ) : null}
      <Outlet />
    </div>
  );
};

export default SettingsBrand;
